// Canal de notificaciones del frontend: mantiene al día el contador de la campana.
//
// Está pensado para que muchos usuarios conectados cuesten casi nada en el servidor:
//
//  - Solo UNA pestaña por navegador consulta (la "líder"); las demás reciben el dato por
//    BroadcastChannel. La líder es la pestaña visible: al volver a una pestaña, esa toma el
//    turno. Con todas ocultas no consulta nadie, porque nadie está mirando la campana.
//  - La consulta es /notificaciones/pulso: va con un ticket firmado, no con el token de
//    Sanctum, y si la versión no cambió el backend responde sin tocar la base.
//  - El intervalo lleva una variación al azar para que no lleguen todas juntas, y crece ante
//    errores (backoff) para no insistir contra un servidor con problemas.
//  - Con Web Push activo el aviso llega solo, y la consulta queda como respaldo espaciado.
//
// Uso: iniciar() una vez con sesión, suscribir(fn) para recibir {n, v, cambio}.

import {config} from '@/config';
import store from '@/store';

const INTERVALO_MS = 30000;          // consulta normal
const INTERVALO_CON_PUSH_MS = 180000; // con Web Push, la consulta es solo respaldo
const INTERVALO_MAX_MS = 300000;     // techo del backoff
const VARIACION = 0.2;               // ±20 % de variación al azar
const LIDER_KEY = 'cw-ntf-lider';
const LIDER_VENCE_MS = 75000;        // si la líder no da señales en este tiempo, otra toma el turno

const estado = {
    iniciado: false,
    ticket: '',
    ticketVence: 0,
    v: null,
    n: 0,
    push: '',          // llave pública VAPID, '' si el servidor no tiene push
    pushActivo: false, // este navegador está suscrito
    timer: null,
    fallos: 0,
    id: Math.random().toString(36).slice(2),
    canal: null,
    oyentes: [],
};

function token() {
    const auth = store.getters.authInfo || {};
    return auth.token || '';
}

function emitir(cambio) {
    estado.oyentes.forEach(function (fn) {
        try { fn({n: estado.n, v: estado.v, cambio: cambio}); } catch (e) { /* un oyente roto no frena a los demás */ }
    });
}

function aplicar(v, n, desdeOtraPestana) {
    const cambio = estado.v !== null && v !== estado.v;
    estado.v = v;
    if (typeof n === 'number') estado.n = n;
    emitir(cambio);
    if (!desdeOtraPestana && estado.canal) {
        estado.canal.postMessage({tipo: 'estado', v: estado.v, n: estado.n});
    }
}

// ── Líder entre pestañas ────────────────────────────────────────────────────────

function leerLider() {
    try { return JSON.parse(localStorage.getItem(LIDER_KEY) || 'null'); } catch (e) { return null; }
}

function marcarLider() {
    try { localStorage.setItem(LIDER_KEY, JSON.stringify({id: estado.id, ts: Date.now()})); } catch (e) { /* sin storage: cada pestaña consulta, igual funciona */ }
}

function soyLider() {
    if (document.visibilityState !== 'visible') return false;
    const lider = leerLider();
    if (!lider || lider.id === estado.id || Date.now() - lider.ts > LIDER_VENCE_MS) {
        marcarLider();
        return true;
    }
    return false;
}

// ── Consultas ───────────────────────────────────────────────────────────────────

async function pedirCanal() {
    const r = await fetch(config.backendUrl + '/notificaciones/canal', {
        headers: {'Authorization': 'Bearer ' + token(), 'Accept': 'application/json'},
    });
    const json = await r.json();
    if (!json || !json.status) throw new Error('canal');
    const d = json.data || {};
    estado.ticket = d.ticket;
    estado.ticketVence = Date.now() + ((d.ttl || 3600) * 1000) - 60000;
    estado.push = d.push || '';
    aplicar(d.v, d.n, false);
}

async function pulso() {
    if (!estado.ticket || Date.now() > estado.ticketVence) {
        await pedirCanal();
        return;
    }
    const url = config.backendUrl + '/notificaciones/pulso?t=' + encodeURIComponent(estado.ticket) + '&v=' + encodeURIComponent(estado.v);
    const r = await fetch(url, {headers: {'Accept': 'application/json'}, cache: 'no-store'});
    if (r.status === 401) {
        estado.ticket = '';
        await pedirCanal();
        return;
    }
    if (!r.ok) throw new Error('pulso ' + r.status);
    const d = await r.json();
    aplicar(d.v, typeof d.n === 'number' ? d.n : estado.n, false);
}

function programar() {
    clearTimeout(estado.timer);
    const base = estado.pushActivo ? INTERVALO_CON_PUSH_MS : INTERVALO_MS;
    const conBackoff = Math.min(base * Math.pow(2, estado.fallos), INTERVALO_MAX_MS);
    const espera = conBackoff * (1 - VARIACION + Math.random() * VARIACION * 2);
    estado.timer = setTimeout(ciclo, espera);
}

async function ciclo() {
    if (!token()) { detener(); return; }
    if (soyLider()) {
        try {
            await pulso();
            estado.fallos = 0;
        } catch (e) {
            estado.fallos = Math.min(estado.fallos + 1, 4);
        }
    }
    programar();
}

// ── API ─────────────────────────────────────────────────────────────────────────

export function iniciar() {
    if (estado.iniciado || !token()) return;
    estado.iniciado = true;

    if (typeof BroadcastChannel !== 'undefined') {
        estado.canal = new BroadcastChannel('cw-notificaciones');
        estado.canal.onmessage = function (ev) {
            const m = ev.data || {};
            if (m.tipo === 'estado') aplicar(m.v, m.n, true);
            if (m.tipo === 'refrescar') ahora();
        };
    }

    // al volver a la pestaña se consulta de una vez: es cuando alguien va a mirar la campana
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') ahora();
    });

    // con Web Push, el service worker avisa que llegó algo y se consulta en el momento
    if (navigator.serviceWorker) {
        navigator.serviceWorker.addEventListener('message', function (ev) {
            if (ev.data && ev.data.tipo === 'ntf-push') ahora();
        });
    }

    pedirCanal()
        .then(function () { return estadoPushLocal(); })
        .catch(function () { estado.fallos = 1; })
        .finally(programar);
}

export function detener() {
    clearTimeout(estado.timer);
    if (estado.canal) estado.canal.close();
    estado.iniciado = false;
    estado.canal = null;
    estado.ticket = '';
    estado.v = null;
    estado.n = 0;
}

/** Consulta ya (al abrir la campana, al volver a la pestaña, tras marcar como leídas). */
export function ahora() {
    if (!estado.iniciado) return;
    clearTimeout(estado.timer);
    marcarLider();
    ciclo();
}

/** Lo llama la campana después de leer o marcar: ya trae el estado nuevo del backend. */
export function actualizar(v, n) {
    aplicar(v, n, false);
}

export function suscribir(fn) {
    estado.oyentes.push(fn);
    fn({n: estado.n, v: estado.v, cambio: false});
    return function () {
        estado.oyentes = estado.oyentes.filter(function (x) { return x !== fn; });
    };
}

// ── Web Push ────────────────────────────────────────────────────────────────────

/** El servidor tiene push y el navegador lo soporta. */
export function pushSoportado() {
    return !!estado.push && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** En iPhone/iPad el push solo funciona con la app agregada a la pantalla de inicio. */
export function pushRequiereInstalar() {
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const instalada = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    return ios && !instalada;
}

export function pushActivo() {
    return estado.pushActivo;
}

async function estadoPushLocal() {
    if (!pushSoportado()) return;
    try {
        const reg = await navigator.serviceWorker.getRegistration('/');
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        estado.pushActivo = !!sub && Notification.permission === 'granted';
    } catch (e) {
        estado.pushActivo = false;
    }
}

function llaveAUint8(base64) {
    const relleno = '='.repeat((4 - base64.length % 4) % 4);
    const crudo = atob((base64 + relleno).replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from(crudo, function (c) { return c.charCodeAt(0); });
}

/** Pide permiso, suscribe este navegador y lo registra en el backend. */
export async function activarPush() {
    if (!pushSoportado()) throw new Error('Este navegador no admite notificaciones push');
    const permiso = await Notification.requestPermission();
    if (permiso !== 'granted') throw new Error('No se concedió el permiso de notificaciones');

    const reg = await navigator.serviceWorker.register('/sw-notificaciones.js', {scope: '/'});
    await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({userVisibleOnly: true, applicationServerKey: llaveAUint8(estado.push)});
    }

    const r = await fetch(config.backendUrl + '/notificaciones/push/suscribir', {
        method: 'POST',
        headers: {'Authorization': 'Bearer ' + token(), 'Content-Type': 'application/json', 'Accept': 'application/json'},
        body: JSON.stringify(sub.toJSON()),
    });
    const json = await r.json();
    if (!json.status) throw new Error(json.msg || 'No se pudo activar');

    estado.pushActivo = true;
    programar();
    return json.msg;
}

export async function desactivarPush() {
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
        await fetch(config.backendUrl + '/notificaciones/push/desuscribir', {
            method: 'POST',
            headers: {'Authorization': 'Bearer ' + token(), 'Content-Type': 'application/json', 'Accept': 'application/json'},
            body: JSON.stringify({endpoint: sub.endpoint}),
        });
        await sub.unsubscribe();
    }
    estado.pushActivo = false;
    programar();
}
